/* FeaturePermissions.js */
import { useCookies } from "react-cookie";
import { ROLES } from "constants/Constants";

// Feature constants for easy reference and type safety
export const FEATURES = {
  COURSE_UPLOAD: "Course Upload",
  PROGRAM_MANAGEMENT: "Program Management",
  PROGRESS_TRACKING: "Progress Tracking",
  ANALYTICS: "Analytics",
  QUIZ_ASSIGNMENT_GENERATION: "Quiz/Assignment Generation",
  PERSONALISED_CONTENT_GENERATION: "Personalised Content Generation",
  CODE_SANDBOX: "Code Sandbox",
  VIJNATA_COURSES_BASIC: "Vijnata Courses Basic",
  VIJNATA_COURSES_INTERMEDIATE: "Vijnata Courses Intermediate",
  VIJNATA_COURSES_ADVANCED: "Vijnata Courses Advanced",
  COURSE_VIEWING: "Course Viewing",
  LIVE_CLASS: "Live Class",
  CODE_SOLUTION_GENERATOR: "Code Solution Generator",
  SUBJECTIVE_QUIZ: "Subjective Quiz",
  ERP_FEATURES: "ERP Features",
  ACCA_FEATURES: "ACCA",
};

// Role-based feature matrix - defines which roles should have which features by default
export const ROLE_FEATURE_MATRIX = {
  [ROLES.PROGRAM_COORDINATOR]: [
    FEATURES.COURSE_UPLOAD,
    FEATURES.PROGRAM_MANAGEMENT,
    FEATURES.PROGRESS_TRACKING,
    FEATURES.COURSE_VIEWING,
    FEATURES.LIVE_CLASS,
    FEATURES.CODE_SOLUTION_GENERATOR,
    FEATURES.SUBJECTIVE_QUIZ,
    FEATURES.ERP_FEATURES,
    FEATURES.ACCA_FEATURES
  ],
  [ROLES.COURSE_PARTICIPANT]: [
    FEATURES.PROGRESS_TRACKING,
    FEATURES.COURSE_VIEWING,
    FEATURES.LIVE_CLASS,
    FEATURES.ERP_FEATURES,
  ],
  [ROLES.COURSE_MANAGER]: [
    FEATURES.COURSE_UPLOAD,
    FEATURES.PERSONALISED_CONTENT_GENERATION,
    FEATURES.QUIZ_ASSIGNMENT_GENERATION,
    FEATURES.COURSE_VIEWING,
    FEATURES.LIVE_CLASS,
    FEATURES.ERP_FEATURES,
    FEATURES.ACCA_FEATURES
  ],
  [ROLES.ORGANIZATION_COORDINATOR]: [
    FEATURES.COURSE_UPLOAD,
    FEATURES.PROGRAM_MANAGEMENT,
    FEATURES.PROGRESS_TRACKING,
    FEATURES.ANALYTICS,
    FEATURES.PERSONALISED_CONTENT_GENERATION,
    FEATURES.COURSE_VIEWING,
    FEATURES.LIVE_CLASS,
    FEATURES.CODE_SOLUTION_GENERATOR,
    FEATURES.SUBJECTIVE_QUIZ,
    FEATURES.ERP_FEATURES,
    FEATURES.ACCA_FEATURES
  ],
  [ROLES.PLATFORM_COORDINATOR]: [
    FEATURES.COURSE_UPLOAD,
    FEATURES.PROGRAM_MANAGEMENT,
    FEATURES.PROGRESS_TRACKING,
    FEATURES.ANALYTICS,
    FEATURES.QUIZ_ASSIGNMENT_GENERATION,
    FEATURES.PERSONALISED_CONTENT_GENERATION,
    FEATURES.CODE_SANDBOX,
    FEATURES.VIJNATA_COURSES_BASIC,
    FEATURES.VIJNATA_COURSES_INTERMEDIATE,
    FEATURES.VIJNATA_COURSES_ADVANCED,
    FEATURES.COURSE_VIEWING,
    FEATURES.LIVE_CLASS,
    FEATURES.CODE_SOLUTION_GENERATOR,
    FEATURES.SUBJECTIVE_QUIZ,
    FEATURES.ERP_FEATURES,
    FEATURES.ACCA_FEATURES
  ],
};

/**
 * List of features to exclude for testing purposes
 * Add or remove feature names from this array to test different feature exclusions
 * FEATURES.PROGRESS_TRACKING
 */
const EXCLUDED_FEATURES = [];

/**
 * Core feature permission utilities
 */
export class FeaturePermissionManager {
  // constructor(userFeatures = [], userRole = null) {
  //     this.userFeatures = Array.isArray(userFeatures) ? userFeatures : [];
  //     this.userRole = userRole;
  // }
  constructor(userFeatures = [], userRole = null) {
    // Filter out excluded features for testing
    this.userFeatures = Array.isArray(userFeatures)
      ? userFeatures.filter(
        (feature) => !EXCLUDED_FEATURES.includes(feature.featureName),
      )
      : [];
    this.userRole = userRole;
  }

  /**
   * Check if user has a specific feature permission
   * @param {string} featureName - Name of the feature to check
   * @returns {boolean}
   */
  hasFeature(featureName) {
    return this.userFeatures.some(
      (feature) => feature.featureName === featureName,
    );
  }

  /**
   * Check if user has any of the specified features
   * @param {string[]} featureNames - Array of feature names to check
   * @returns {boolean}
   */
  hasAnyFeature(featureNames) {
    return featureNames.some((featureName) => this.hasFeature(featureName));
  }

  /**
   * Check if user has all of the specified features
   * @param {string[]} featureNames - Array of feature names to check
   * @returns {boolean}
   */
  hasAllFeatures(featureNames) {
    return featureNames.every((featureName) => this.hasFeature(featureName));
  }

  /**
   * Get all feature names the user has access to
   * @returns {string[]}
   */
  getUserFeatureNames() {
    return this.userFeatures.map((feature) => feature.featureName);
  }

  /**
   * Get feature by ID
   * @param {string} featureId - Feature ID to search for
   * @returns {Object|null}
   */
  getFeatureById(featureId) {
    return (
      this.userFeatures.find((feature) => feature.featureId === featureId) ||
      null
    );
  }

  /**
   * Check if user should have access to a feature based on their role
   * @param {string} featureName - Feature name to check
   * @returns {boolean}
   */
  shouldHaveFeatureByRole(featureName) {
    const roleFeatures = ROLE_FEATURE_MATRIX[this.userRole] || [];
    return roleFeatures.includes(featureName);
  }

  /**
   * Get all features the user should have based on their role
   * @returns {string[]}
   */
  getExpectedFeaturesByRole() {
    return ROLE_FEATURE_MATRIX[this.userRole] || [];
  }

  /**
   * Check if user has role permission
   * @param {string} userRole - User's role ID
   * @param {Array} allowedRoles - Array of allowed role IDs
   * @returns {boolean}
   */
  hasRolePermission(allowedRoles) {
    return allowedRoles.includes(this.userRole);
  }

  /**
   * Filter menu items based on user role and features
   * @param {Array} items - Menu items to filter
   * @returns {Array} Filtered menu items
   */
  getAuthorizedMenuItems(items) {
    return items
      .filter((item) => {
        const hasRole = this.hasRolePermission(item.roles);
        const hasFeature =
          !item.featureRequired || this.hasFeature(item.featureRequired);
        return hasRole && hasFeature;
      })
      .map((item) => {
        if (item.hasDropdown && Array.isArray(item.dropdownOptions)) {
          return {
            ...item,
            dropdownOptions: item.dropdownOptions.filter((option) => {
              const hasRole = this.hasRolePermission(option.roles);
              const hasFeature =
                !option.featureRequired ||
                this.hasFeature(option.featureRequired);
              return hasRole && hasFeature;
            }),
          };
        }
        return item;
      });
  }
}

/**
 * React hook for feature permissions
 * @returns {Object} Permission manager instance and utility functions
 */
export const useFeaturePermissions = () => {
  const [cookies] = useCookies();

  const features = Array.isArray(cookies.features) ? cookies.features : [];
  const userRole = cookies.roleId;

  const permissionManager = new FeaturePermissionManager(features, userRole);

  return {
    // Permission manager instance
    permissionManager,

    // Direct access to commonly used methods
    hasFeature: (featureName) => permissionManager.hasFeature(featureName),
    hasAnyFeature: (featureNames) =>
      permissionManager.hasAnyFeature(featureNames),
    hasAllFeatures: (featureNames) =>
      permissionManager.hasAllFeatures(featureNames),

    // Utility data
    userFeatures: features,
    userRole,
    allUserFeatureNames: permissionManager.getUserFeatureNames(),
  };
};

/**
 * Higher-order component for feature-based conditional rendering
 * @param {string|string[]} requiredFeatures - Feature name(s) required
 * @param {Object} options - Additional options
 * @returns {Function} HOC function
 */
export const withFeaturePermission = (requiredFeatures, options = {}) => {
  return (WrappedComponent) => {
    const FeatureProtectedComponent = (props) => {
      const { hasFeature, hasAnyFeature, hasAllFeatures } =
        useFeaturePermissions();

      const { requireAll = false, fallback = null } = options;

      let hasPermission = false;

      if (typeof requiredFeatures === "string") {
        hasPermission = hasFeature(requiredFeatures);
      } else if (Array.isArray(requiredFeatures)) {
        hasPermission = requireAll
          ? hasAllFeatures(requiredFeatures)
          : hasAnyFeature(requiredFeatures);
      }

      return hasPermission ? <WrappedComponent {...props} /> : fallback;
    };

    FeatureProtectedComponent.displayName = `withFeaturePermission(${WrappedComponent.displayName || WrappedComponent.name})`;

    return FeatureProtectedComponent;
  };
};

/**
 * Component for conditional rendering based on feature permissions
 * @param {Object} props - Component props
 * @param {string|string[]} props.feature - Feature name(s) to check
 * @param {boolean} props.requireAll - Whether all features are required (for arrays)
 * @param {React.ReactNode} props.children - Content to render if permission granted
 * @param {React.ReactNode} props.fallback - Content to render if permission denied
 * @returns {React.ReactNode}
 */
export const FeatureGuard = ({
  feature,
  requireAll = false,
  children,
  fallback = null,
}) => {
  const { hasFeature, hasAnyFeature, hasAllFeatures } = useFeaturePermissions();

  let hasPermission = false;

  if (typeof feature === "string") {
    hasPermission = hasFeature(feature);
  } else if (Array.isArray(feature)) {
    hasPermission = requireAll
      ? hasAllFeatures(feature)
      : hasAnyFeature(feature);
  }

  return hasPermission ? children : fallback;
};

// Utility functions for non-React contexts
export const createFeaturePermissionManager = (userFeatures, userRole) => {
  return new FeaturePermissionManager(userFeatures, userRole);
};

export const checkFeaturePermission = (userFeatures, featureName) => {
  const manager = new FeaturePermissionManager(userFeatures);
  return manager.hasFeature(featureName);
};
